import { PageHero } from "@/components/PageHero";
import { Section } from "@/components/Section";
import { useSeo } from "@/lib/seo";
import {
  Anchor,
  Box,
  Divider,
  Grid,
  Stack,
  Text,
  Title,
} from "@repo/ui/core";
import { Link } from "react-router";

const LAST_UPDATED = "March 22, 2026";
const EFFECTIVE_DATE = "March 22, 2026";
const CONTACT_EMAIL = "privacy@null.autos";
const COMPANY_NAME = "Null Autos";
const COMPANY_LEGAL = "Null Autos Inc";

const sections = [
  { id: "scope", label: "Scope of This Policy" },
  { id: "information-we-collect", label: "Information We Collect" },
  { id: "how-we-use", label: "How We Use Your Information" },
  { id: "sharing", label: "Information Sharing" },
  { id: "cookies", label: "Cookies & Tracking" },
  { id: "data-retention", label: "Data Retention" },
  { id: "security", label: "Data Security" },
  { id: "your-rights", label: "Your Rights" },
  { id: "international", label: "International Transfers" },
  { id: "children", label: "Children's Privacy" },
  { id: "changes", label: "Changes to This Policy" },
  { id: "contact", label: "Contact Us" },
];

function PolicySection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box component="section" id={id} pt="xs">
      <Title order={2} size="1.375rem" fw={700} mb="md">
        {title}
      </Title>
      <Stack gap="md">{children}</Stack>
      <Divider mt="xl" color="dark.7" />
    </Box>
  );
}

function PolicyParagraph({ children }: { children: React.ReactNode }) {
  return (
    <Text size="sm" c="dimmed" lh={1.8}>
      {children}
    </Text>
  );
}

function PolicySubhead({ children }: { children: React.ReactNode }) {
  return (
    <Text fw={600} size="sm" mt="xs">
      {children}
    </Text>
  );
}

export default function Privacy() {
  useSeo("/privacy");

  return (
    <>
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        description="How we collect, use, and protect your information when you visit the null.autos website."
      >
        <Text size="sm" c="dimmed">
          Effective: {EFFECTIVE_DATE} &nbsp;|&nbsp; Last updated: {LAST_UPDATED}
        </Text>
      </PageHero>

      <Section>
        <Grid gutter={60}>
          {/* Table of contents - sticky sidebar on desktop */}
          <Grid.Col span={{ base: 12, md: 3 }}>
            <Box
              style={{
                position: "sticky",
                top: "calc(var(--na-nav-h) + 24px)",
              }}
            >
              <Text fw={600} size="sm" mb="md" tt="uppercase" lts="0.06em" c="dimmed">
                Contents
              </Text>
              <Stack gap={6}>
                {sections.map((s) => (
                  <Anchor
                    key={s.id}
                    href={`#${s.id}`}
                    size="sm"
                    c="dimmed"
                    underline="never"
                    style={{ transition: "color 150ms ease" }}
                  >
                    {s.label}
                  </Anchor>
                ))}
              </Stack>
              <Divider my="xl" color="dark.7" />
              <Text size="xs" c="dimmed" lh={1.7}>
                Questions about our privacy practices?{" "}
                <Anchor href={`mailto:${CONTACT_EMAIL}`} size="xs">
                  {CONTACT_EMAIL}
                </Anchor>
              </Text>
            </Box>
          </Grid.Col>

          {/* Policy content */}
          <Grid.Col span={{ base: 12, md: 9 }}>
            <Stack gap="xl">
              {/* Intro */}
              <Box>
                <PolicyParagraph>
                  {COMPANY_LEGAL} (&ldquo;{COMPANY_NAME},&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard personal information when you visit our marketing website at null.autos (the &ldquo;Website&rdquo;).
                </PolicyParagraph>
                <PolicyParagraph>
                  By accessing or using this Website, you agree to the collection and use of information in accordance with this policy. If you disagree with any part of this policy, please discontinue use of the Website.
                </PolicyParagraph>
                <Divider mt="xl" color="dark.7" />
              </Box>

              {/* 0. Scope */}
              <PolicySection id="scope" title="1. Scope of This Policy">
                <PolicyParagraph>
                  This Privacy Policy applies <strong>only</strong> to personal information collected through the null.autos marketing website, including its pages, contact and demo request forms, and cookies. It does not apply to personal information collected through {COMPANY_NAME} software products, developer tools, APIs, CI/CD platform, or any other {COMPANY_NAME} services.
                </PolicyParagraph>
                <PolicyParagraph>
                  If you are a customer or user of a {COMPANY_NAME} software product, the privacy terms governing that product are set out in the applicable product agreement, data processing agreement (DPA), or privacy notice provided to you at the time of onboarding. Please refer to those documents or contact your {COMPANY_NAME} account representative for details.
                </PolicyParagraph>
              </PolicySection>

              {/* 2. Information We Collect */}
              <PolicySection id="information-we-collect" title="2. Information We Collect">
                <PolicySubhead>Information You Provide Directly</PolicySubhead>
                <PolicyParagraph>
                  We collect information you voluntarily provide when you interact with this Website, such as when you submit a contact form, book a demo, or sign up for marketing communications:
                </PolicyParagraph>
                <Stack gap={6} pl="md">
                  {[
                    "Contact details: name, email address, company name, and job title",
                    "Message content: enquiries or feedback you submit via our contact forms",
                    "Demo request details: your preferred meeting time and any notes you include",
                    "Marketing preferences: whether you wish to receive product updates and newsletters",
                  ].map((item) => (
                    <Text key={item} size="sm" c="dimmed" lh={1.7}>
                      &bull;&nbsp;&nbsp;{item}
                    </Text>
                  ))}
                </Stack>

                <PolicySubhead>Information We Collect Automatically</PolicySubhead>
                <PolicyParagraph>
                  When you browse this Website, we automatically collect certain technical information to operate and improve it:
                </PolicyParagraph>
                <Stack gap={6} pl="md">
                  {[
                    "Log data: IP address, browser type, operating system, referring URLs, pages visited, and timestamps",
                    "Device information: device type, screen resolution, and browser language",
                    "Usage data: pages and sections viewed, time spent on each page, and navigation paths",
                    "Performance data: page load times and any error events recorded by our analytics tooling",
                  ].map((item) => (
                    <Text key={item} size="sm" c="dimmed" lh={1.7}>
                      &bull;&nbsp;&nbsp;{item}
                    </Text>
                  ))}
                </Stack>
                <PolicyParagraph>
                  We do not collect data about software builds, CI/CD pipeline runs, emulator sessions, API usage, or any other platform telemetry through this Website. That data is handled separately under the applicable product agreement.
                </PolicyParagraph>
              </PolicySection>

              {/* 3. How We Use */}
              <PolicySection id="how-we-use" title="3. How We Use Your Information">
                <PolicyParagraph>
                  We use the information collected through this Website for the following purposes, always on a lawful basis:
                </PolicyParagraph>
                <Stack gap={8}>
                  {[
                    {
                      head: "Responding to Enquiries",
                      body: "Processing contact form submissions and demo requests so we can follow up with you in a timely manner.",
                    },
                    {
                      head: "Marketing & Communications",
                      body: "Sending product updates, newsletters, and promotional content, only with your consent or where permitted by applicable law. You may opt out at any time via the unsubscribe link in any email or by contacting us directly.",
                    },
                    {
                      head: "Website Improvement",
                      body: "Analysing browsing patterns and page performance to improve the Website's content, structure, and user experience. We aggregate and anonymise this data where possible.",
                    },
                    {
                      head: "Security",
                      body: "Detecting and preventing abuse, unauthorised access, and other harmful activity directed at this Website.",
                    },
                    {
                      head: "Legal Compliance",
                      body: "Meeting our obligations under applicable laws and regulations, including GDPR and CCPA.",
                    },
                  ].map(({ head, body }) => (
                    <Box key={head}>
                      <PolicySubhead>{head}</PolicySubhead>
                      <PolicyParagraph>{body}</PolicyParagraph>
                    </Box>
                  ))}
                </Stack>
              </PolicySection>

              {/* 4. Sharing */}
              <PolicySection id="sharing" title="4. Information Sharing">
                <PolicyParagraph>
                  We do not sell your personal information. We share your information only in the following limited circumstances:
                </PolicyParagraph>
                <Stack gap={8}>
                  {[
                    {
                      head: "Service Providers",
                      body: "We share data with trusted third-party vendors who help us operate this Website, including analytics platforms, email delivery services, and CRM tools. These vendors are bound by data processing agreements and may only process your data as directed by us.",
                    },
                    {
                      head: "Business Transfers",
                      body: "In the event of a merger, acquisition, reorganisation, or sale of assets, your information may be transferred. We will notify you before your personal data is transferred and becomes subject to a different privacy policy.",
                    },
                    {
                      head: "Legal Requirements",
                      body: "We may disclose your information if required by law or in response to valid legal process (court orders, subpoenas, government requests). Where legally permitted, we will notify you of such requests.",
                    },
                    {
                      head: "Protection of Rights",
                      body: "We may disclose information when we believe it is necessary to investigate, prevent, or act on illegal activities, suspected fraud, or potential threats to safety.",
                    },
                    {
                      head: "With Your Consent",
                      body: "We may share your information for any other purpose with your explicit consent.",
                    },
                  ].map(({ head, body }) => (
                    <Box key={head}>
                      <PolicySubhead>{head}</PolicySubhead>
                      <PolicyParagraph>{body}</PolicyParagraph>
                    </Box>
                  ))}
                </Stack>
              </PolicySection>

              {/* 5. Cookies */}
              <PolicySection id="cookies" title="5. Cookies & Tracking Technologies">
                <PolicyParagraph>
                  We use cookies and similar tracking technologies to enhance your experience on this Website and understand how it is used.
                </PolicyParagraph>
                <Stack gap={8}>
                  {[
                    {
                      head: "Essential Cookies",
                      body: "Required for the Website to function correctly, including CSRF protection and load-balancing cookies. You cannot opt out of these without affecting your ability to use the Website.",
                    },
                    {
                      head: "Analytics Cookies",
                      body: "Help us understand how visitors interact with this Website (pages visited, time on site, referral sources). We use privacy-preserving analytics and do not share this data with advertising networks.",
                    },
                    {
                      head: "Preference Cookies",
                      body: "Remember your settings and preferences (such as UI theme or language) across sessions.",
                    },
                  ].map(({ head, body }) => (
                    <Box key={head}>
                      <PolicySubhead>{head}</PolicySubhead>
                      <PolicyParagraph>{body}</PolicyParagraph>
                    </Box>
                  ))}
                </Stack>
                <PolicyParagraph>
                  You can control cookies through your browser settings. Disabling certain cookies may affect Website functionality. We honour Do Not Track (DNT) browser signals where applicable.
                </PolicyParagraph>
              </PolicySection>

              {/* 6. Retention */}
              <PolicySection id="data-retention" title="6. Data Retention">
                <PolicyParagraph>
                  We retain personal information collected through this Website for as long as necessary to fulfil the purposes outlined in this policy, comply with our legal obligations, and resolve any disputes. Indicative retention periods:
                </PolicyParagraph>
                <Stack gap={6} pl="md">
                  {[
                    "Contact and demo request data: retained for up to 2 years from last interaction, or until you request deletion",
                    "Marketing preference records: retained while you remain subscribed, plus a suppression record to honour opt-outs",
                    "Website analytics data: retained in aggregated, anonymised form for up to 26 months",
                    "Security and access logs: retained for 12 months to support incident investigation",
                  ].map((item) => (
                    <Text key={item} size="sm" c="dimmed" lh={1.7}>
                      &bull;&nbsp;&nbsp;{item}
                    </Text>
                  ))}
                </Stack>
                <PolicyParagraph>
                  When data is no longer needed, we securely delete or anonymise it. You may request earlier deletion of your personal data subject to our legal retention obligations.
                </PolicyParagraph>
              </PolicySection>

              {/* 7. Security */}
              <PolicySection id="security" title="7. Data Security">
                <PolicyParagraph>
                  We implement industry-standard technical and organisational measures to protect your information against unauthorised access, loss, or alteration:
                </PolicyParagraph>
                <Stack gap={6} pl="md">
                  {[
                    "Encryption in transit using TLS 1.2+ for all data communications",
                    "Encryption at rest for databases and backup data",
                    "Role-based access controls (RBAC) with least-privilege enforcement",
                    "Regular security assessments of our Website infrastructure",
                    "Audit logs for all administrative access to personal data",
                  ].map((item) => (
                    <Text key={item} size="sm" c="dimmed" lh={1.7}>
                      &bull;&nbsp;&nbsp;{item}
                    </Text>
                  ))}
                </Stack>
                <PolicyParagraph>
                  Despite our best efforts, no method of transmission over the Internet is 100% secure. In the event of a data breach that affects your rights, we will notify you within 72 hours as required by applicable law.
                </PolicyParagraph>
              </PolicySection>

              {/* 8. Your Rights */}
              <PolicySection id="your-rights" title="8. Your Rights">
                <PolicyParagraph>
                  Depending on your location, you may have the following rights regarding your personal information. We honour these rights for all users regardless of jurisdiction.
                </PolicyParagraph>
                <Stack gap={8}>
                  {[
                    {
                      head: "Access",
                      body: "Request a copy of the personal data we hold about you.",
                    },
                    {
                      head: "Correction",
                      body: "Request that we correct inaccurate or incomplete personal data.",
                    },
                    {
                      head: "Deletion (Right to be Forgotten)",
                      body: "Request deletion of your personal data, subject to our legal retention obligations.",
                    },
                    {
                      head: "Restriction",
                      body: "Request that we restrict processing of your personal data in certain circumstances.",
                    },
                    {
                      head: "Data Portability",
                      body: "Request your personal data in a structured, machine-readable format.",
                    },
                    {
                      head: "Objection",
                      body: "Object to processing of your personal data for direct marketing or legitimate interest purposes.",
                    },
                    {
                      head: "Withdraw Consent",
                      body: "Where processing is based on consent, withdraw it at any time without affecting the lawfulness of prior processing.",
                    },
                    {
                      head: "California Residents (CCPA)",
                      body: "You have the right to know what categories of personal information we collect and share, the right to delete, and the right to opt out of the sale of personal information. We do not sell personal information.",
                    },
                  ].map(({ head, body }) => (
                    <Box key={head}>
                      <PolicySubhead>{head}</PolicySubhead>
                      <PolicyParagraph>{body}</PolicyParagraph>
                    </Box>
                  ))}
                </Stack>
                <PolicyParagraph>
                  To exercise any of these rights, contact us at{" "}
                  <Anchor href={`mailto:${CONTACT_EMAIL}`} size="sm">
                    {CONTACT_EMAIL}
                  </Anchor>
                  . We will respond within 30 days (or as required by applicable law). We may verify your identity before fulfilling the request.
                </PolicyParagraph>
              </PolicySection>

              {/* 9. International */}
              <PolicySection id="international" title="9. International Data Transfers">
                <PolicyParagraph>
                  {COMPANY_NAME} operates infrastructure in the United States and European Union. If you access this Website from outside these regions, information you submit (such as contact form data) may be transferred to and processed in a country where data protection laws may differ from your own.
                </PolicyParagraph>
                <PolicyParagraph>
                  For transfers of personal data from the European Economic Area (EEA) or United Kingdom to countries without an adequacy decision, we rely on appropriate safeguards such as Standard Contractual Clauses (SCCs) approved by the European Commission. A copy of these clauses is available on request.
                </PolicyParagraph>
              </PolicySection>

              {/* 10. Children */}
              <PolicySection id="children" title="10. Children's Privacy">
                <PolicyParagraph>
                  This Website is not directed to individuals under the age of 16 (or the applicable age of digital consent in your jurisdiction). We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately at{" "}
                  <Anchor href={`mailto:${CONTACT_EMAIL}`} size="sm">
                    {CONTACT_EMAIL}
                  </Anchor>{" "}
                  and we will take prompt steps to delete it.
                </PolicyParagraph>
              </PolicySection>

              {/* 11. Changes */}
              <PolicySection id="changes" title="11. Changes to This Policy">
                <PolicyParagraph>
                  We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements. When we make material changes, we will:
                </PolicyParagraph>
                <Stack gap={6} pl="md">
                  {[
                    "Update the \"Last updated\" date at the top of this page",
                    "Display a prominent notice on the Website for at least 30 days",
                  ].map((item) => (
                    <Text key={item} size="sm" c="dimmed" lh={1.7}>
                      &bull;&nbsp;&nbsp;{item}
                    </Text>
                  ))}
                </Stack>
                <PolicyParagraph>
                  Your continued use of the Website after the effective date of the revised policy constitutes your acceptance of the changes. We encourage you to review this policy periodically.
                </PolicyParagraph>
              </PolicySection>

              {/* 12. Contact */}
              <Box component="section" id="contact" pt="xs">
                <Title order={2} size="1.375rem" fw={700} mb="md">
                  12. Contact Us
                </Title>
                <Stack gap="md">
                  <PolicyParagraph>
                    If you have questions, concerns, or requests regarding this Privacy Policy or our data practices for this Website, please contact our privacy team:
                  </PolicyParagraph>
                  <Stack gap={6} pl="md">
                    <Text size="sm" c="dimmed" lh={1.7}>
                      <strong>Email:</strong>{" "}
                      <Anchor href={`mailto:${CONTACT_EMAIL}`} size="sm">
                        {CONTACT_EMAIL}
                      </Anchor>
                    </Text>
                    <Text size="sm" c="dimmed" lh={1.7}>
                      <strong>Company:</strong> {COMPANY_LEGAL}
                    </Text>
                  </Stack>
                  <PolicyParagraph>
                    If you are located in the EEA or UK and believe we have not addressed your concern adequately, you have the right to lodge a complaint with your local data protection authority (e.g., the Information Commissioner&apos;s Office in the UK, or the relevant supervisory authority in your EU member state).
                  </PolicyParagraph>
                  <Text size="sm" c="dimmed" lh={1.7}>
                    Want to review our Terms of Use?{" "}
                    <Anchor component={Link} to="/terms" size="sm">
                      View Terms
                    </Anchor>
                  </Text>
                </Stack>
              </Box>
            </Stack>
          </Grid.Col>
        </Grid>
      </Section>
    </>
  );
}
